/**
 * @file: useUpdateOrder.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { siteInspectionStagedToCommitPayload } from '@euler/app/flows/task/functions/task-detail/util';
import { useAppLoading } from '@euler/components/loading/Provider';
import { normalizedExtendedPropertiesFromOrderExtra } from '@euler/functions/order/extendedPropertyUtil';
import { TroubleLightInfo, UpdateOrderRequest } from '@euler/model';
import { AbnormalLevel, SiteInspectionType } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { orderTagsEqual, showAlert } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useOrderFlowContext } from '../../../OrderFlowContext';
import { vehicleIdentityFromForm } from '../../../functions/vinHelper';

export const useUpdateOrder = () => {
  const { orderService } = useServiceFactory();

  const {
    formController: { form },
    dirty$,
    orderDetail$,
    ocrImageInfo,
    lastVehicleInfo$,
    basicInfo,
    finish,
    onUpdated,
    storeConfiguration,
  } = useOrderFlowContext();

  const { event$ } = useOrderContext();

  const user = useCurrentUser();
  const appLoading = useAppLoading();

  const [detail] = useBehaviorSubject(orderDetail$);
  const [isDirty] = useBehaviorSubject(dirty$);

  const [licensePlateNoOcrImageInfo] = useBehaviorSubject(
    ocrImageInfo.licensePlateNo$,
  );
  const [vinOcrImageInfo] = useBehaviorSubject(ocrImageInfo.vin$);
  const [lastVehicleInfo] = useBehaviorSubject(lastVehicleInfo$);

  return usePersistFn(async () => {
    if (basicInfo == null || user == null || !isDirty) return;
    try {
      let hasVehicleInfoChanges = false;

      const request: UpdateOrderRequest = {};
      if (
        form.serviceType?.value != null &&
        form.serviceType?.value !== basicInfo.orderType
      ) {
        request.orderType = form.serviceType.value;
      }
      if (
        form.serviceAgentInfo.value?.serviceAgentId !== basicInfo.serviceAgentId
      ) {
        request.serviceAgentId = form.serviceAgentInfo.value?.serviceAgentId;
      }

      if (
        basicInfo.isMainOrder &&
        form.licensePlateNo.value !== basicInfo.licensePlateNo
      ) {
        request.licensePlateNo = form.licensePlateNo.value;
        request.licensePlateNoImgUrl = licensePlateNoOcrImageInfo?.imageUrl;
      }
      const identity = vehicleIdentityFromForm({
        isNonStandardVin: form.isNonStandardVin.value,
        vin: form.vin.value,
        chassisNo: form.chassisNo.value,
      });
      if (basicInfo.isMainOrder && identity.vin !== basicInfo.vin) {
        request.vin = identity.vin;
        request.vinImgUrl = vinOcrImageInfo?.imageUrl;
      }
      if (
        basicInfo.isMainOrder &&
        identity.chassisNo !== (basicInfo.chassisNo ?? null)
      ) {
        // null clears it — the vehicle turned out to have a real VIN after all.
        request.chassisNo = identity.chassisNo;
      }
      if (
        !basicInfo.hasChildOrders &&
        form.tags != null &&
        !orderTagsEqual(form.tags.value, basicInfo.tags)
      ) {
        request.tags = form.tags.value?.join(',');
      }

      if (
        basicInfo.isMainOrder &&
        form.mileage.value !== basicInfo.vehicleMileage
      ) {
        request.vehicleMileage = form.mileage.value;
      }

      if (basicInfo.isMainOrder && form.vehicleInfo.value != null) {
        if (
          form.vehicleInfo.value?.vehicleName &&
          form.vehicleInfo.value?.vehicleName !== basicInfo.vehicleName
        ) {
          request.vehicleName = form.vehicleInfo.value.vehicleName;
          hasVehicleInfoChanges = true;
        }
        if (
          form.vehicleInfo.value?.vehicleBrandName &&
          form.vehicleInfo.value?.vehicleBrandName !==
            basicInfo.vehicleBrandName
        ) {
          request.vehicleBrandName = form.vehicleInfo.value.vehicleBrandName;
          hasVehicleInfoChanges = true;
        }
        if (form.vehicleInfo.value?.vehicleModel !== basicInfo.vehicleModel) {
          request.vehicleModel = form.vehicleInfo.value.vehicleModel;
          hasVehicleInfoChanges = true;
        }
        if (
          form.vehicleInfo.value?.vehicleModelYear !==
          basicInfo.vehicleModelYear
        ) {
          request.vehicleModelYear = form.vehicleInfo.value.vehicleModelYear;
          hasVehicleInfoChanges = true;
        }
        if (
          form.vehicleInfo.value?.vehicleManufacturer !==
          basicInfo.vehicleManufacturer
        ) {
          request.vehicleManufacturer =
            form.vehicleInfo.value.vehicleManufacturer;
          hasVehicleInfoChanges = true;
        }
        if (
          form.vehicleInfo.value?.vehicleDisplacement !==
          basicInfo.vehicleDisplacement
        ) {
          request.vehicleDisplacement =
            form.vehicleInfo.value.vehicleDisplacement;
          hasVehicleInfoChanges = true;
        }
        if (
          form.vehicleInfo.value?.vehicleFuelType !== basicInfo.vehicleFuelType
        ) {
          request.vehicleFuelType = form.vehicleInfo.value.vehicleFuelType;
          hasVehicleInfoChanges = true;
        }
        if (hasVehicleInfoChanges && lastVehicleInfo != null) {
          request.vehicleInfo = lastVehicleInfo;
        }
      }
      if (
        basicInfo.isEntryOrder &&
        form.dashboardImgUrl.value &&
        form.dashboardImgUrl.value !== basicInfo.dashboardImageUrl
      ) {
        request.dashboardImgUrl = form.dashboardImgUrl.value;
      }

      if (
        basicInfo.isMainOrder &&
        form.customerName.value != null &&
        form.customerName.value.trim() !== basicInfo.customerName
      ) {
        request.customerName = form.customerName.value;
      }
      if (
        basicInfo.isMainOrder &&
        form.customerMobile.value != null &&
        form.customerMobile.value.trim() !== basicInfo.customerMobile
      ) {
        request.customerMobile = form.customerMobile.value;
      }
      if (
        basicInfo.isMainOrder &&
        form.customerType.value != null &&
        form.customerType.value !== basicInfo.customerType
      ) {
        request.customerType = form.customerType.value;
      }
      if (
        basicInfo.isMainOrder &&
        detail?.signImageUrl != null &&
        detail?.signImageUrl !== basicInfo.signImgUrl
      ) {
        request.signImgUrl = detail.signImageUrl;
        request.signedAt = detail.signedAt;
      }
      if (
        basicInfo.isMainOrder &&
        form.carwashType.value !== basicInfo.carwashType
      ) {
        request.carwashType = form.carwashType.value;
      }
      if (
        basicInfo.isMainOrder &&
        form.returnType.value !== basicInfo.returnType
      ) {
        request.returnType = form.returnType.value;
      }
      if (
        basicInfo.isMainOrder &&
        form.oldPartDisposalType.value !== basicInfo.oldPartDisposalType
      ) {
        request.oldPartDisposalType = form.oldPartDisposalType.value;
      }
      if (
        basicInfo.isMainOrder &&
        form.reservationType.value !== basicInfo.reservationType
      ) {
        request.reservationType = form.reservationType.value;
      }
      if (
        basicInfo.isMainOrder &&
        form.insuranceExpiryDate.value !== basicInfo.insuranceExpiryDate
      ) {
        request.insuranceExpiryDate = form.insuranceExpiryDate.value;
      }

      const estimatedFinishTime = form.estimatedFinishTime.value
        ? formatDateTime(
            dateFromValue(form.estimatedFinishTime.value),
            'yyyy-MM-dd HH:mm:ss',
          )
        : '';
      const existingEstimatedFinishTime = basicInfo.estimatedFinishTime
        ? formatDateTime(
            dateFromValue(basicInfo.estimatedFinishTime),
            'yyyy-MM-dd HH:mm:ss',
          )
        : '';
      if (estimatedFinishTime !== existingEstimatedFinishTime) {
        request.estimatedFinishTime = estimatedFinishTime;
        request.estimatedFinishTimeUpdateReason =
          form.estimatedFinishTimeUpdateReason.value;
        request.estimatedFinishTimeUpdateRemark =
          form.estimatedFinishTimeUpdateRemark.value;
      }

      if (form.remark.value !== basicInfo.remark) {
        request.remark = form.remark.value;
      }

      request.detail = {
        serviceSubjects: !basicInfo.hasChildOrders
          ? detail.serviceSubjects
          : undefined,
        extendedPropDefs:
          storeConfiguration.extendedOrderProperties ?? undefined,
        extendedProps: normalizedExtendedPropertiesFromOrderExtra(
          detail,
          storeConfiguration,
        ),
        troubleLights: basicInfo.isEntryOrder
          ? detail.preInspection
              ?.filter(
                x =>
                  x.inspectionType === SiteInspectionType.Dashboard &&
                  x.abnormalLevel !== AbnormalLevel.Fine,
              )
              .map<TroubleLightInfo>(inspection => ({
                code: inspection.siteCode,
                name: inspection.siteName,
              }))
          : undefined,
        facadeRecordMediaCount: basicInfo.isEntryOrder
          ? detail.preInspection
              ?.filter(x => x.inspectionType === SiteInspectionType.Facade)
              .flatMap(x => x.items.flatMap(t => t.medias)).length
          : undefined,
      };

      if (basicInfo.isEntryOrder) {
        request.preInspection = detail.preInspection?.map(staged =>
          siteInspectionStagedToCommitPayload(staged, {
            flowId: '',
            technicianId: user.user.memberId,
            technicianName: user.user.name,
            inspectionType: staged.inspectionType,
            siteId: staged.siteId,
            siteName: staged.siteName,
          }),
        );
      }

      appLoading.show();
      __DEV__ && console.log(basicInfo.orderNo);
      __DEV__ && console.log(JSON.stringify(request, null, 2));
      const order = await orderService.updateOrder(basicInfo.orderNo, request);
      event$.next({
        type: 'updated',
        orderNo: basicInfo.orderNo,
        taskNo: basicInfo.taskNo,
        isMainOrder: basicInfo.isMainOrder,
        extra: { request, order, hasVehicleInfoChanges },
      });
      onUpdated?.(order);
      finish();
    } catch (e) {
      console.error(e);
      void showAlert('出错了', (e as Error).message);
    } finally {
      appLoading.hide();
    }
  });
};
